import { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import useBooks from "api/BookResource/useBooks";
import { CustomPaginator } from "components";
import Admin2 from "layouts/Admin2";
import useAuthStore from "store/authStore";
import { BsBookmarks, BsCloudDownload } from "react-icons/bs";
import useExportBooks from "api/BookResource/useExportBooks";
import { FaCircleNotch } from "react-icons/fa";
import AdminPageLoader from "components/AdminPageLoader";
import useGenres from "api/GenreResource/useGenres";
import useFetchCategories from "api/CategoryResource/useFetchCategories";
import useFetchPublishers from "api/PublisherResource/useFetchPublishers";
import { Role } from "utils/server-constants/roles";
import { BookFilterProvider } from "./BookFilter/BookFilterContext";
import BulkActions from "./BulkActions";
import ClearFilters from "admin/Filters/ClearFilters";
import RowsPerPage from "admin/Filters/RowsPerPage";
import { formData } from "./BookFilter/booksQueryParams";
import Search from "admin/Filters/Search";
import useBooksFilters from "./BookFilter/useBooksFilters";
import BookFilter from "./BookFilter";
import BookCoverPreview from "components/BookCoverPreview";
import BookRowExpansion from "./BookRowExpansion";
import BookActionsColumn from "./BookActionsColumn";
import { activeCurrencies } from "utils";

const AllBooks2 = () => {
	const filters = useBooksFilters();
	const hasRoles = useAuthStore((state) => state.hasRoles);
	const [selectedBooks, setSelectedBooks] = useState([]);
	const [expandedRows, setExpandedRows] = useState(null);

	// Get number of active filters excluding pagination params
	const numberOfActiveFilters = filters?.allSearchParams
		?.filter((param) => !filters?.paginationParams?.includes(param))
		?.filter((param) => filters[param] !== "")?.length;

	// Data fetching hooks
	const { data: genres, isLoading: isLoadingGenres } = useGenres("yes");
	const { data: categories, isLoading: isLoadingCategories } = useFetchCategories("yes");
	const { data: publishers, isLoading: isLoadingPublishers } = useFetchPublishers();
	const { mutateAsync: mutateExport, isLoading: isExporting } = useExportBooks();

	// Fetch books with filters
	const { data: books, isLoading: isBooksLoading } = useBooks({
		page: filters.page,
		rows_per_page: filters.rowsPerPage,
		from_backend: true,
		"filter[is_active]": filters.isActive,
		"filter[category_is]": filters.categoryIs,
		"filter[genre_is]": filters.genreIs,
		"filter[isFree]": filters.isFree,
		"filter[publisher_is]": filters.publisherIs,
		"filter[pricing_model_is]": filters.pricingModelIs,
		without_file_collection: filters.withoutFileCollection,
		with_file_collection: filters.withFileCollection,
		searchTerm: filters.search,
	});

	const totalRecords = books?.data?.total_records || 0;
	const booksList = books?.data?.records || [];

	const handleDownloadBooksMetadata = async () => {
		await mutateExport({ format: "csv", filters });
	};

	const handleSelectionChange = (e) => {
		setSelectedBooks(e.value);
	};

	// Column Templates
	const bookCoverTemplate = (rowData) => {
		return (
			<BookCoverPreview
				coverUrl={rowData?.book_cover?.url}
				title={rowData?.title}
				thumbnailWidth={64}
				thumbnailHeight={80}
			/>
		);
	};

	const titleTemplate = (rowData) => {
		return (
			<div className="flex flex-col">
				<span className="font-semibold text-gray-900">{rowData?.title}</span>
				{rowData?.isbn && <span className="text-xs text-gray-400">ISBN: {rowData?.isbn}</span>}
			</div>
		);
	};

	const authorTemplate = (rowData) => {
		return (
			<div className="flex flex-col">
				<span className="text-sm text-gray-700">{rowData?.author}</span>
				{rowData?.publisher && (
					<span className="text-xs text-gray-400 truncate max-w-[150px]">{rowData?.publisher}</span>
				)}
			</div>
		);
	};

	const categoryTemplate = (rowData) => {
		return <span className="text-sm text-gray-600">{rowData?.category?.name || "N/A"}</span>;
	};

	const priceTemplate = (rowData) => {
		if (rowData?.isFree) {
			return <Tag value="Free" severity="success" className="text-xs" />;
		}
		return (
			<div className="flex flex-col gap-1">
				<div className="text-sm">
					<span className="font-semibold">{activeCurrencies.USD.label}:</span> $
					{rowData?.price || 0}
				</div>
				<div className="text-sm">
					<span className="font-semibold">{activeCurrencies.ZWL.label}:</span> $
					{rowData?.zwl_price || 0}
				</div>
			</div>
		);
	};

	const statusTemplate = (rowData) => {
		return (
			<div className="flex flex-col gap-1">
				<Tag
					value={rowData?.is_active ? "Active" : "Inactive"}
					severity={rowData?.is_active ? "success" : "warning"}
					className="text-xs"
				/>
				{!rowData?.has_book_file && <Tag value="No File" severity="danger" className="text-xs" />}
			</div>
		);
	};

	const formatTemplate = (rowData) => {
		const format = rowData?.format || "digital";
		const formatLabels = {
			digital: { label: "Digital", severity: "info" },
			physical: { label: "Physical", severity: "warning" },
			both: { label: "Both", severity: "success" },
		};
		const { label, severity } = formatLabels[format] || formatLabels.digital;
		return <Tag value={label} severity={severity} className="text-xs" />;
	};

	const actionsTemplate = (rowData) => {
		return (
			<BookActionsColumn
				book={rowData}
				categories={categories}
				genres={genres}
				isAdmin={hasRoles([Role.SuperAdmin, Role.Admin])}
			/>
		);
	};

	// Row expansion template
	const rowExpansionTemplate = (data) => {
		return (
			<BookRowExpansion
				book={data}
				categories={categories}
				genres={genres}
				isAdmin={hasRoles([Role.SuperAdmin, Role.Admin])}
			/>
		);
	};

	if (isBooksLoading || isLoadingCategories || isLoadingGenres || isLoadingPublishers) {
		return <AdminPageLoader />;
	}

	return (
		<Admin2 fullWidth>
			<BookFilterProvider>
				<div className="my-4 space-y-6 w-full">
					{/* Header */}
					<div className="flex items-center justify-between gap-4 bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
						<div className="flex items-center gap-3">
							<div className="bg-akgreenest p-2.5 rounded-xl text-white shadow-sm">
								<BsBookmarks size={24} />
							</div>
							<div>
								<h3 className="text-xl font-bold text-gray-900">Book List</h3>
								<p className="text-sm text-gray-500 font-medium">{totalRecords} books available</p>
							</div>
						</div>

						<div className="flex items-center gap-2">
							{hasRoles([Role.SuperAdmin, Role.Admin]) && (
								<button
									type="button"
									className="h-11 px-5 gap-2 bg-white border border-gray-200 hover:border-akgreen text-gray-700 font-semibold rounded-xl flex items-center justify-center transition-all active:scale-95 shadow-sm"
									onClick={handleDownloadBooksMetadata}
									disabled={isExporting}
								>
									{isExporting ? (
										<FaCircleNotch size={18} className="animate-spin text-akgreener" />
									) : (
										<BsCloudDownload size={18} className="text-akgreener" />
									)}
									Download CSV
								</button>
							)}
						</div>
					</div>

					{/* Search and Filters */}
					<div className="flex flex-wrap items-center gap-3 bg-gray-50/50 p-3 rounded-2xl border border-gray-100">
						<div className="flex-1 min-w-[240px]">
							<Search filters={filters} formData={formData} />
						</div>
						<div className="flex items-center gap-2">
							<RowsPerPage filters={filters} formData={formData} />
							<div className="h-8 w-px bg-gray-200 mx-1" />
							<BookFilter
								genres={genres}
								categories={categories}
								publishers={publishers}
								numberOfActiveFilters={numberOfActiveFilters}
							/>
							<ClearFilters numberOfActiveFilters={numberOfActiveFilters} filters={filters} />
						</div>
					</div>

					{/* Bulk Actions */}
					{hasRoles([Role.SuperAdmin, Role.Admin, Role.SeniorPublisher]) &&
						selectedBooks.length > 0 && (
							<div className="flex items-center gap-3 bg-akgreen/5 p-3 rounded-xl border border-akgreen/20">
								<span className="text-sm font-bold text-akgreener bg-akgreen/10 px-3 py-1 rounded-full">
									{selectedBooks.length} selected
								</span>
								<BulkActions
									books={selectedBooks.map((book) => book.id)}
									resetBooks={() => setSelectedBooks([])}
								/>
							</div>
						)}

					{/* DataTable */}
					<div className="shadow-sm">
						<DataTable
							value={booksList}
							selection={selectedBooks}
							onSelectionChange={handleSelectionChange}
							selectionMode={
								hasRoles([Role.SuperAdmin, Role.Admin, Role.SeniorPublisher]) ? "checkbox" : null
							}
							expandedRows={expandedRows}
							onRowToggle={(e) => setExpandedRows(e.data)}
							rowExpansionTemplate={rowExpansionTemplate}
							dataKey="id"
							tableClassName="font-sans text-sm"
							loading={isBooksLoading}
							emptyMessage="No books found"
							responsiveLayout="scroll"
						>
							{hasRoles([Role.SuperAdmin, Role.Admin, Role.SeniorPublisher]) && (
								<Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
							)}
							<Column expander style={{ width: "3rem" }} />
							<Column
								header="Cover"
								body={bookCoverTemplate}
								style={{ width: "80px" }}
								className="text-center"
							/>
							<Column
								header="Title"
								body={titleTemplate}
								style={{ minWidth: "200px" }}
								sortable
								sortField="title"
							/>
							<Column header="Author" body={authorTemplate} style={{ minWidth: "150px" }} />
							<Column header="Category" body={categoryTemplate} style={{ width: "120px" }} />
							<Column
								header="Format"
								body={formatTemplate}
								style={{ width: "90px" }}
								className="text-center"
							/>
							<Column header="Price" body={priceTemplate} style={{ width: "130px" }} />
							<Column
								header="Status"
								body={statusTemplate}
								style={{ width: "100px" }}
								className="text-center"
							/>
							{hasRoles([Role.SuperAdmin, Role.Admin, Role.SeniorPublisher, Role.Publisher]) && (
								<Column
									header="Actions"
									body={actionsTemplate}
									style={{ width: "100px" }}
									className="text-center"
								/>
							)}
						</DataTable>

						{/* Pagination */}
						<div className="flex justify-center mt-6">
							<CustomPaginator
								firstPage={filters?.firstPage}
								rowsPerPage={filters?.rowsPerPage}
								setFirstPage={(val) => filters?.changeFilter("firstPage", val)}
								setRowsPerPage={(val) => filters?.changeFilter("rowsPerPage", val)}
								totalRecords={totalRecords}
								setPage={(val) => filters?.changeFilter("page", val)}
								showRowsPerPageDropdown={false}
							/>
						</div>
					</div>
				</div>
			</BookFilterProvider>
		</Admin2>
	);
};

export default AllBooks2;
